import { gql } from "graphql-request";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import LoadingIndicator from "src/components/loadingindicator/LoadingIndicator";
import SelectorWrapper from "src/components/selectorwrapper/SelectorWrapper";
import SearchSvg from "src/components/svg/SearchSvg";
import { useFilminfo } from "src/lib/client/useFilminfo";
import { useLocationContext } from "src/lib/contexts/LocationContext";
import { type Location } from "src/lib/movieinfo/movieinfotypes";
import styles from "./LocationSelector.module.css";

const SEARCH_QUERY = gql`
	query ($searchText: String) {
		cinemaQuery {
			searchForLocations (searchText: $searchText) {
				__typename
				name
			}
		}
	}
`;

const GEOLOC_QUERY = gql`
	query($lat: Float, $long: Float ) {
    cinemaQuery {
        closestLocations(  latitude: $lat, longitude: $long ) {
            __typename
            name
        }
    }
}
`;

//#region [Props]
type LocationSelectorProps = {
    canUseGeoLocation: boolean;
};
//#endregion

const hotlinkLocations: Location[] = [
    { name: "Oslo" },
    { name: "Oslo og omegn" },
    { name: "Bergen" },
    { name: "Trondheim" },
    { name: "Stavanger" },
    { name: "Sandnes" },
    { name: "Sandvika" },
    { name: "Lillestrøm" },
    { name: "Ski" },
    { name: "Drammen" },
    { name: "Kristiansand S" }
];

//#region [Component]
export default function LocationSelector({ canUseGeoLocation }: LocationSelectorProps) {
    const locationContext = useLocationContext();
    const [searchText, setSearchText] = useState("");
    const { fiData } = useFilminfo(SEARCH_QUERY, { searchText }, { active: !!searchText });
    const [isLoadingGeoPos, setIsLoadingGeoPos] = useState(canUseGeoLocation ? false : true);
    const [isGeoPosEnabled, setIsGeoPosEnabled] = useState(false);

    return <SelectorWrapper onClose={() => locationContext.setShowLocationDialog(false)} heading="Velg sted">

        <div className={styles.SLocationSelector}>

            <div className={styles.SSearchField}>
                <input className={styles.SSearchInput} type="text" placeholder="Søk sted" onChange={(e) => setSearchText(e.target.value)} ref={input => { if (input) input.focus() }} />
                <div className={styles.SIconPlaceholder}>
                    <SearchSvg className={styles.SSearchSvg} />
                </div>
            </div>
            {canUseGeoLocation && <GeoLocationPosition setIsLoadingGeoposition={setIsLoadingGeoPos} setIsGeoPosEnabled={setIsGeoPosEnabled} />}
            {(!canUseGeoLocation || (!isLoadingGeoPos && !isGeoPosEnabled)) && <div className={styles.SResultArea}>
                {(fiData?.cinemaQuery?.searchForLocations ?? hotlinkLocations).slice(0, hotlinkLocations.length).map((loc, index) => (
                    <div className={styles.SResultItem} key={index} onClick={() => {
                        locationContext.setLocation(loc!.name!);
                        locationContext.setShowLocationDialog(false);
                    }}>{loc!.name}</div>
                ))}

            </div>}
        </div>
    </SelectorWrapper>;
}
//#endregion

type GeoLocationPositionProps = {
    setIsLoadingGeoposition: Dispatch<SetStateAction<boolean>>;
    setIsGeoPosEnabled: Dispatch<SetStateAction<boolean>>;
}

//#region [Component]
function GeoLocationPosition({ setIsLoadingGeoposition, setIsGeoPosEnabled }: GeoLocationPositionProps) {
    const locationContext = useLocationContext();
    const [geoLocPos, setGeoLocPos] = useState<GeolocationPosition | undefined | number>(); // if number, user has denied location or some other error has ocurred.
    const [isLoadingPosition, setIsLoadingPosition] = useState(false);

    useEffect(() => {
        if ("geolocation" in navigator) {
            setIsLoadingPosition(true);
            setIsLoadingGeoposition(true);
            navigator.geolocation.getCurrentPosition(currPos => {
                console.log("Got geoloc", currPos);
                setIsLoadingPosition(false);
                setIsLoadingGeoposition(false);
                setGeoLocPos(currPos);
                setIsGeoPosEnabled(true);
            }, (positionError) => {
                console.error("Postion error", positionError);
                setIsLoadingPosition(false);
                setIsLoadingGeoposition(false);

                setGeoLocPos(positionError.code);
            });
        }
    }, [setIsGeoPosEnabled, setIsLoadingGeoposition]);

    const { fiData, fiLoading } = useFilminfo(
        GEOLOC_QUERY,
        {
            lat: (geoLocPos as GeolocationPosition | undefined)?.coords?.latitude,
            long: (geoLocPos as GeolocationPosition | undefined)?.coords?.longitude
        },
        { active: typeof geoLocPos !== "number" && !!geoLocPos }
    );

    if (typeof geoLocPos === "number") {
        console.error("Geopos is number", geoLocPos);
        return null;
    }



    return <div className={styles.SResultArea}>
        <h5 className={styles.SCloseLocationsHeader}>Kinoer i nærheten:</h5>
        {(isLoadingPosition || fiLoading) && <LoadingIndicator size="large" position="InlineCenter" />}
        {!(isLoadingPosition || fiLoading) && <>
            {(fiData?.cinemaQuery?.closestLocations?.length ?? 0) === 0 && <>Ingen kinoer funnet</>}
            {(fiData?.cinemaQuery?.closestLocations)?.map((loc, index) => (
                <div className={styles.SResultItem} key={index} onClick={() => {
                    locationContext.setLocation(loc!.name!);
                    locationContext.setShowLocationDialog(false);
                }}>{loc!.name}</div>
            ))}
        </>}
    </div>

}
//#endregion
